import { useEffect } from "react";
import { useBlocker } from "react-router-dom";

const DEFAULT_MESSAGE =
  "You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.";

/**
 * Guards against losing unsaved work. While `when` is true:
 *  - in-app navigation (React Router links, navigate(), sidebar, back/forward)
 *    triggers a confirm() popup before the route actually changes;
 *  - closing / reloading the browser tab triggers the native browser prompt.
 *
 * Requires the app to use a data router (createBrowserRouter / RouterProvider)
 * so that useBlocker is available.
 */
export default function useUnsavedChanges(when, message = DEFAULT_MESSAGE) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !!when && currentLocation.pathname !== nextLocation.pathname
  );

  // In-app navigation: ask, then proceed or stay.
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm(message)) blocker.proceed();
    else blocker.reset();
  }, [blocker, message]);

  // Safety net: if the guard turns off while a navigation is parked, let it through.
  useEffect(() => {
    if (!when && blocker.state === "blocked") blocker.proceed();
  }, [when, blocker]);

  // Tab close / reload / navigating to an external URL.
  useEffect(() => {
    if (!when) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [when]);
}
